import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Table,
  Space,
  Form,
  Input,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '@/lib/api';
import { NumericInput, withDevModalFilePath } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/schedule/ScheduleToolsSection.tsx');

type ScheduleTool = {
  id: string;
  scheduleId: string;
  toolName: string;
  requiredQuantity: number;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
};

interface Props {
  scheduleId: string;
  hasTemplate?: boolean;
}

export function ScheduleToolsSection({ scheduleId, hasTemplate }: Props) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<ScheduleTool | null>(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // データ取得
  const { data: scheduleTools = [], refetch } = trpc.schedule.getTools.useQuery(
    { scheduleId },
    { enabled: Boolean(scheduleId) }
  );
  const scheduleToolRows = useMemo<ScheduleTool[]>(
    () => (Array.isArray(scheduleTools) ? (scheduleTools as unknown as ScheduleTool[]) : []),
    [scheduleTools]
  );

  // Mutations
  const addToolMutation = trpc.schedule.addTool.useMutation({
    onSuccess: () => {
      messageApi.success('工具を追加しました');
      setAddModalOpen(false);
      form.resetFields();
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const updateToolMutation = trpc.schedule.updateTool.useMutation({
    onSuccess: () => {
      messageApi.success('工具情報を更新しました');
      setEditModalOpen(false);
      form.resetFields();
      setSelectedTool(null);
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const removeToolMutation = trpc.schedule.removeTool.useMutation({
    onSuccess: () => {
      messageApi.success('工具を削除しました');
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const inheritToolsMutation = trpc.schedule.inheritToolsFromTemplate.useMutation({
    onSuccess: (result) => {
      messageApi.success(result.message);
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  // ハンドラー
  const handleAddTool = () => {
    form.resetFields();
    setAddModalOpen(true);
  };

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      addToolMutation.mutate({
        scheduleId,
        toolName: values.toolName,
        requiredQuantity: values.requiredQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleEdit = (record: ScheduleTool) => {
    setSelectedTool(record);
    form.setFieldsValue({
      toolName: record.toolName,
      requiredQuantity: record.requiredQuantity,
      notes: record.notes,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedTool) return;

    try {
      const values = await form.validateFields();
      updateToolMutation.mutate({
        scheduleId,
        toolId: selectedTool.id,
        toolName: values.toolName,
        requiredQuantity: values.requiredQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDelete = (record: ScheduleTool) => {
    Modal.confirm({
      title: '工具を削除しますか？',
      content: `「${record.toolName}」をこのスケジュールから削除します。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        removeToolMutation.mutate({
          scheduleId,
          toolId: record.id,
        });
      },
    });
  };

  const handleInheritFromTemplate = () => {
    Modal.confirm({
      title: 'テンプレートから工具を引き継ぎますか？',
      content: 'テンプレートに設定されている工具を、このスケジュールに追加します。',
      okText: '引き継ぐ',
      cancelText: 'キャンセル',
      onOk: () => {
        inheritToolsMutation.mutate({ scheduleId });
      },
    });
  };

  const columns: ColumnsType<ScheduleTool> = [
    {
      title: '工具名',
      dataIndex: 'toolName',
      key: 'toolName',
    },
    {
      title: '必要数量',
      dataIndex: 'requiredQuantity',
      key: 'requiredQuantity',
      width: 120,
      render: (qty) => `${qty}個`,
    },
    {
      title: '備考',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (val) => val || '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            編集
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card
        title={
          <Space>
            <ToolOutlined />
            使用工具
          </Space>
        }
        extra={
          <Space>
            {hasTemplate && (
              <Button
                icon={<CopyOutlined />}
                onClick={handleInheritFromTemplate}
                loading={inheritToolsMutation.isPending}
              >
                テンプレートから引き継ぎ
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTool}>
              工具を追加
            </Button>
          </Space>
        }
      >
        <Table<ScheduleTool>
          columns={columns}
          dataSource={scheduleToolRows}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* 工具追加モーダル */}
      <Modal
        title="工具を追加"
        open={addModalOpen}
        onOk={handleAddSubmit}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={addToolMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="工具名"
            name="toolName"
            rules={[{ required: true, message: '工具名を入力してください' }]}
          >
            <Input placeholder="例: トルクレンチ、デジタルマルチメーター" />
          </Form.Item>

          <Form.Item
            label="必要数量"
            name="requiredQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
            initialValue={1}
          >
            <NumericInput min={1} style={{ width: '100%' }} addonAfter="個" />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <Input.TextArea rows={3} placeholder="備考を入力（任意）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 工具編集モーダル */}
      <Modal
        title="工具情報を編集"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalOpen(false);
          form.resetFields();
          setSelectedTool(null);
        }}
        confirmLoading={updateToolMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="工具名"
            name="toolName"
            rules={[{ required: true, message: '工具名を入力してください' }]}
          >
            <Input placeholder="例: トルクレンチ、デジタルマルチメーター" />
          </Form.Item>

          <Form.Item
            label="必要数量"
            name="requiredQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
          >
            <NumericInput min={1} style={{ width: '100%' }} addonAfter="個" />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <Input.TextArea rows={3} placeholder="備考を入力（任意）" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
